// @owners { team: patients-team }
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';
import { useNextAvailableDateAddressId } from '~shared/features/next-available-date/hooks/useNextAvailableDateAddressId';
import { nextAvailableDatesQueryKeys } from '~shared/features/next-available-date/queries/nextAvailableDatesQueryKeys';
import { getNextAvailableDates } from '~shared/features/next-available-date/selectors/getNextAvailableDates';
import { getNextAvailableDatesByPrescriptionID } from '~shared/features/next-available-date/selectors/getNextAvailableDatesByPrescriptionID';
import { type NextAvailableDateMapping } from '~shared/reducers/nextAvailableDates';
import { useSelectorShared } from '~shared/store';
import { type NextAvailableDate } from '~shared/types';

type NextAvailableDatesForPrescriptionsParams = {
  addressID?: number;
  facilityID?: number;
  prescriptionID?: number;
  shipmentID?: number;
};

type NextAvailableDatesForPrescriptionsResponse = {
  error?: any;
  /** True when any NAD-scoped React Query observer is in `error` status (query cache), not Redux emptiness. */
  isError: boolean;
  /** True while any NAD-scoped query is fetching (`useIsFetching` on `nextAvailableDatesQueryKeys.all`). */
  isLoading: boolean;
  /**
   * True when not fetching and no NAD query is in error. Can be true on first paint before any NAD query
   * has run (idle), so it does not mean “fresh NAD loaded successfully”.
   */
  isSuccess: boolean;
  nextAvailableDate: NextAvailableDate | null;
  nextAvailableDates?: NextAvailableDateMapping;
  nextAvailableDatesByPrescriptionID: Record<number, NextAvailableDate>;
};

const useNextAvailableDatesScopeHasError = () => {
  const queryClient = useQueryClient();
  return useSyncExternalStore(
    (onStoreChange) => queryClient.getQueryCache().subscribe(onStoreChange),
    () => {
      const queries = queryClient.getQueryCache().findAll({ queryKey: nextAvailableDatesQueryKeys.all });
      return queries.some((q) => q.state.status === 'error') ? 'has-error' : 'no-error';
    },
    () => 'no-error',
  );
};

/**
 * Next-available-date **payloads** (`nextAvailableDate`, `nextAvailableDates`, `nextAvailableDatesByPrescriptionID`)
 * are read from **Redux** until strategy A (read NAD from the React Query cache). Status flags (`isLoading`,
 * `isError`, `isSuccess`) come from **React Query** (in-flight + query-cache error state for the NAD key scope).
 *
 * **Hybrid behavior:** `isError === true` only means an NAD-scoped query failed in the RQ cache; Redux may still
 * hold valid stale dates. Do not hide or clear NAD-dependent UI solely on `isError`, or you risk discarding good
 * data. Prefer `isLoading` when you need to avoid acting on dates that may still be refetching.
 */
export const useNextAvailableDatesForPrescriptions = (
  params: NextAvailableDatesForPrescriptionsParams = {},
): NextAvailableDatesForPrescriptionsResponse => {
  const { prescriptionID } = params;
  const nextAvailableDateAddressID = useNextAvailableDateAddressId();
  const nextAvailableDatesByPrescriptionID = useSelectorShared((state) =>
    getNextAvailableDatesByPrescriptionID(state, { nextAvailableDateAddressID }),
  );
  const nextAvailableDates = useSelectorShared(getNextAvailableDates);
  const nadScopeHasError = useNextAvailableDatesScopeHasError();
  const isLoading = useIsFetching({ queryKey: nextAvailableDatesQueryKeys.all }) > 0;
  const isError = nadScopeHasError === 'has-error';
  const isSuccess = !isLoading && !isError;

  return {
    isError,
    isLoading,
    isSuccess,
    nextAvailableDate: prescriptionID ? nextAvailableDatesByPrescriptionID[prescriptionID] : null,
    nextAvailableDates,
    nextAvailableDatesByPrescriptionID,
  };
};
